import type { ToolCall } from '@kanbu/schema';
import { ToolNames } from '@kanbu/schema/contracts';
import { useLingui } from '@lingui/react/macro';
import {
  BookOpenText,
  FileSearch,
  GitCompare,
  Gift,
  Globe,
  Layers,
  Library,
  Link2,
  type LucideIcon,
  Package,
  PackageCheck,
  Puzzle,
  ShoppingBag,
  Tags,
  Truck,
  Wrench,
} from 'lucide-react';
import { useCallback } from 'react';

/**
 * Product-lookup tools (by id / external id / ean / isbn) share one icon and
 * phrasing — the identifier the model used is an internal detail.
 */
const PRODUCT_LOOKUP_TOOLS: ReadonlySet<string> = new Set([
  ToolNames.GetShopProductDetails,
  /** Retired lookups — kept so persisted conversations still render. */
  ToolNames.GetShopProductByProductId,
  ToolNames.GetShopProductByExternalId,
  ToolNames.GetShopProductByEan,
  ToolNames.GetShopProductByIsbn,
]);

/**
 * Icon per tool type for the activity trail. Covers the built-in retrieval
 * tools, every shop research tool, and a generic wrench fallback for
 * custom / MCP tools that have no dedicated visual identity.
 */
export function toolActivityIcon(name: string): LucideIcon {
  switch (name) {
    case ToolNames.DocumentSearch:
      return FileSearch;
    case ToolNames.FindDocuments:
      return Library;
    case ToolNames.GetDocumentOverview:
      return BookOpenText;
    case ToolNames.WebSearch:
      return Globe;
    case ToolNames.FetchUrl:
      return Link2;
    case ToolNames.GetShopProducts:
      return ShoppingBag;
    case ToolNames.GetSimilarProducts:
      return Layers;
    case ToolNames.GetShopCategories:
      return Tags;
    case ToolNames.GetShopProductAccessories:
      return Puzzle;
    case ToolNames.GetShopDeliveryOptions:
      return Truck;
    case ToolNames.GetShopProductAvailability:
      return PackageCheck;
    case ToolNames.GetShopProductGifts:
      return Gift;
    case ToolNames.CompareShopProducts:
      return GitCompare;

    default:
      return PRODUCT_LOOKUP_TOOLS.has(name) ? Package : Wrench;
  }
}

export interface ToolPhrase {
  /** Present-tense line shown while the tool runs (the mutating verb). */
  running: string;
  /** Past-tense roll-up shown once the tool settled. */
  past: string;
}

/**
 * Returns a resolver for a tool call's running / past phrases. The
 * model-authored `label` (when present) wins for the running line — it is
 * written for the user and is more specific than our defaults; the past-tense
 * roll-up always uses our own wording so completed steps read consistently.
 *
 * The templates live inside the hook so the Lingui macro binds them to the
 * component-scoped `t` from `useLingui`.
 */
export function useToolPhrase(): (toolCall: ToolCall) => ToolPhrase {
  const { t } = useLingui();

  return useCallback(
    (toolCall: ToolCall): ToolPhrase => {
      let running: string;
      let past: string;

      switch (toolCall.name) {
        case ToolNames.DocumentSearch:
          running = t`Searching the knowledge base…`;
          past = t`Searched the knowledge base`;
          break;
        case ToolNames.FindDocuments:
          running = t`Browsing the knowledge base…`;
          past = t`Browsed the knowledge base`;
          break;
        case ToolNames.GetDocumentOverview:
          running = t`Reviewing a document…`;
          past = t`Reviewed a document`;
          break;
        case ToolNames.WebSearch:
          running = t`Searching the web…`;
          past = t`Searched the web`;
          break;
        case ToolNames.FetchUrl:
          running = t`Reading a page…`;
          past = t`Read a page`;
          break;
        case ToolNames.GetShopProducts:
          running = t`Searching products…`;
          past = t`Searched products`;
          break;
        case ToolNames.GetSimilarProducts:
          running = t`Looking for similar products…`;
          past = t`Found similar products`;
          break;
        case ToolNames.GetShopCategories:
          running = t`Browsing categories…`;
          past = t`Browsed categories`;
          break;
        case ToolNames.GetShopProductAccessories:
          running = t`Finding accessories…`;
          past = t`Found accessories`;
          break;
        case ToolNames.GetShopDeliveryOptions:
          running = t`Checking delivery options…`;
          past = t`Checked delivery options`;
          break;
        case ToolNames.GetShopProductAvailability:
          running = t`Checking availability…`;
          past = t`Checked availability`;
          break;
        case ToolNames.GetShopProductGifts:
          running = t`Checking gifts…`;
          past = t`Checked gifts`;
          break;
        case ToolNames.CompareShopProducts:
          running = t`Comparing products…`;
          past = t`Compared products`;
          break;

        default:
          if (PRODUCT_LOOKUP_TOOLS.has(toolCall.name)) {
            running = t`Looking up a product…`;
            past = t`Looked up a product`;
          } else {
            running = t`Working…`;
            past = t`Ran a tool`;
          }
      }

      const label = toolCall.label?.trim();

      return { running: label || running, past };
    },
    [t],
  );
}
